import { ICellRendererParams, IRowNode } from "@ag-grid-community/core";
import { Tooltip } from "@chakra-ui/react";
import { dataAttr } from "@chakra-ui/utils";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import React from "react";
import { StatusDot } from "shared/components/status/StatusDot";
import { DataTableCellLink } from "shared/components/table/data/DataTableCellLink";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import {
  DataTableColumnContext,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import { RenderCellValueProps } from "shared/components/table/data/DataTableTypes";
import { DataTableCellActions } from "shared/components/table/data/internal/DataTableCellActions";
import { DataTableCellBadge } from "shared/components/table/data/internal/DataTableCellBadge";
import { isDataTableSensitive } from "shared/components/table/data/internal/DataTableInternalUtils";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";

export interface DataTableCellContentRendererProps {
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
  row: IRowNode<GenericTableRow>;
  cell: GenericTableCell;
  params: ICellRendererParams;
}

export const DataTableCellContentRenderer =
  React.memo<DataTableCellContentRendererProps>(
    ({ tableContext, columnContext, row, cell, params }) => {
      const {
        getActionsForCell,
        onButtonClick,
        getLinkInfoForCell,
        getLinkInfoForCellBadge,
        renderCellValue,
      } = tableContext;

      const isAggregate = Boolean(
        columnContext.column.aggregatable_type && row.expanded,
      );

      const renderProps: RenderCellValueProps = {
        type: "data",
        tableContext,
        columnContext,
        row,
        cell,
      };

      const linkInfo = getLinkInfoForCell?.(renderProps);
      const badgeLinkInfo = getLinkInfoForCellBadge?.(renderProps);
      const actionInfo = getActionsForCell?.(renderProps);

      const renderFn = renderCellValue ?? defaultDataTableRenderCellValue;
      const cellValue = renderFn(renderProps, undefined, params);

      const valueContent = cellValue ? (
        typeof cellValue === "string" ? (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            key="value"
            className="mb-value-content"
          >
            {cellValue}
          </div>
        ) : (
          cellValue
        )
      ) : null;

      const statusContent = cell.status ? (
        <StatusDot key="status" type={cell.status.type} />
      ) : null;

      const badgeContent = cell.badge ? (
        <DataTableCellBadge
          key="badge"
          badge={cell.badge}
          linkInfo={badgeLinkInfo}
        />
      ) : null;

      const tooltipContent = cell.tooltip ? (
        <Tooltip key="tooltip" label={cell.tooltip.message} placement="top">
          <InfoCircleIcon w="16px" h="16px" color="gray.400" />
        </Tooltip>
      ) : null;

      const wrapContent = (content: React.ReactNode) => (
        <div
          className="mb-dt-cell-content"
          data-is-aggregate={dataAttr(isAggregate)}
          data-ls-disabled={dataAttr(isDataTableSensitive(columnContext, cell))}
          data-private={dataAttr(isDataTableSensitive(columnContext, cell))}
        >
          {content}
        </div>
      );

      if (!statusContent && !valueContent && !badgeContent && !tooltipContent) {
        return wrapContent(null);
      }

      let content = wrapContent(
        <>
          {statusContent}
          {valueContent}
          {badgeContent}
          {tooltipContent}
        </>,
      );

      if (linkInfo) {
        content = (
          <DataTableCellLink
            tableContext={tableContext}
            columnContext={columnContext}
            cell={cell}
            linkInfo={linkInfo}
            row={row}
            onButtonClick={onButtonClick}
          >
            {content}
          </DataTableCellLink>
        );
      }

      if (actionInfo) {
        content = (
          <DataTableCellActions
            tableContext={tableContext}
            row={row}
            columnContext={columnContext}
            cell={cell}
            actions={actionInfo}
            onButtonClick={onButtonClick}
          >
            {content}
          </DataTableCellActions>
        );
      }

      return content;
    },
  );
DataTableCellContentRenderer.displayName = "DataTableCellContentRenderer";
